import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import type { IntlLike } from '../clientPresentation';
import {
  MAX_RECRUIT_SCORE,
  RECRUIT_AXES,
  summariseRecruitScores,
  useRecruitScores,
  writeRecruitScores
} from '../recruitScore';
import type { RecruitAxis, RecruitScores } from '../recruitScore';
import { useCanEditClientNotes } from '../clientNotesStore';

/**
 * 候選人評估's scoring popup — five rows of 1–5 and the live 總分 beneath.
 * Opened from the drawer's radar and from 招募 IDEA's 他做得來嗎 card; both
 * write through writeRecruitScores, so every chart, pill and total follows.
 */

/** 總分 + band, e.g. 「總分 28 分 · 暫緩」, or 「已評 3/5」 until all five are in. */
export const RecruitTotal = (props: { scores: RecruitScores; intl: IntlLike }) => {
  const { scores, intl } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const summary = summariseRecruitScores(scores);

  return (
    <div className="clientv2-recruit-summary-head">
      <span className="clientv2-recruit-summary-label">{t('clientv2-recruit-total')}</span>
      {summary.total === null ? (
        <span className="clientv2-recruit-total is-partial" data-testid="recruit-total" data-rated={summary.rated}>
          {t('clientv2-recruit-total-partial', { rated: summary.rated, count: RECRUIT_AXES.length })}
        </span>
      ) : (
        <span
          className={`clientv2-recruit-total is-${summary.tier}`}
          data-testid="recruit-total"
          data-total={summary.total}
          data-tier={summary.tier}
        >
          <strong>{t('clientv2-recruit-total-points', { score: summary.total })}</strong>
          {' · '}
          {t(`clientv2-recruit-tier-${summary.tier}`)}
        </span>
      )}
    </div>
  );
};

export type RecruitScoreDialogProps = {
  open: boolean;
  onClose: () => void;
  clientId: string;
  intl: IntlLike;
};

const RecruitScoreDialog = (props: RecruitScoreDialogProps) => {
  const { open, onClose, clientId, intl } = props;
  const t = (id: string) => intl.formatMessage({ id });
  const scores = useRecruitScores(clientId);
  // No factsheet, nowhere to save a score: the rows stay readable, not tappable.
  const canSave = useCanEditClientNotes(clientId);

  const setScore = (axis: RecruitAxis, value: number | null) => {
    const next: RecruitScores = { ...scores };
    if (value === null) delete next[axis];
    else next[axis] = value;
    writeRecruitScores(clientId, next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      // It can open over 招募 IDEA's bottom sheet, which already holds the body
      // scroll lock — the same reason the drawer's DISC playbook sets this.
      disableScrollLock
      data-testid="recruit-radar-dialog"
      aria-labelledby="clientv2-recruit-radar-title"
    >
      <DialogTitle id="clientv2-recruit-radar-title">{t('clientv2-recruit-radar-title')}</DialogTitle>
      <DialogContent>
        {!canSave && (
          <p className="clientv2-recruit-readonly-note" data-testid="recruit-score-readonly">
            {t('clientv2-notes-no-factsheet')}
          </p>
        )}
        {RECRUIT_AXES.map(axis => (
          <div className="clientv2-recruit-score-row" key={axis}>
            <span className="clientv2-recruit-score-label">{t(`clientv2-recruit-axis-${axis}-full`)}</span>
            <div
              className="clientv2-recruit-score-picks"
              role="radiogroup"
              aria-label={t(`clientv2-recruit-axis-${axis}-full`)}
            >
              {Array.from({ length: MAX_RECRUIT_SCORE }, (_, index) => index + 1).map(value => {
                const selected = scores[axis] === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid="recruit-score"
                    data-axis={axis}
                    data-value={value}
                    className={`clientv2-recruit-score${selected ? ' is-selected' : ''}`}
                    disabled={!canSave}
                    // Tapping the chosen score again clears it.
                    onClick={() => setScore(axis, selected ? null : value)}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {/* 總分, live: it moves with every tap above. */}
        <div className="clientv2-recruit-dialog-total" data-testid="recruit-radar-total">
          <RecruitTotal scores={scores} intl={intl} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" data-testid="recruit-radar-close" onClick={onClose}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecruitScoreDialog;
